import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const TaxRateSettings = () => {
  const [taxRates, setTaxRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [formData, setFormData] = useState({ name: '', rate: '', description: '', is_default: false });
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTaxRates();
  }, []);

  const fetchTaxRates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('tax_rates')
        ?.select('*')
        ?.order('name');
      if (error) throw error;
      setTaxRates(data || []);
    } catch (err) {
      console.error('Error fetching tax rates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingRate(null);
    setFormData({ name: '', rate: '', description: '', is_default: false });
    setError('');
    setShowForm(true);
  };

  const handleOpenEdit = (rate) => {
    setEditingRate(rate);
    setFormData({ name: rate?.name, rate: rate?.rate, description: rate?.description || '', is_default: rate?.is_default });
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData?.name || formData?.rate === '') {
      setError('Name and rate are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (formData?.is_default) {
        // Clear existing default
        await supabase?.from('tax_rates')?.update({ is_default: false })?.eq('is_default', true);
      }

      if (editingRate) {
        const { error } = await supabase
          ?.from('tax_rates')
          ?.update({
            name: formData?.name,
            rate: parseFloat(formData?.rate),
            description: formData?.description,
            is_default: formData?.is_default,
            updated_at: new Date()?.toISOString()
          })
          ?.eq('id', editingRate?.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          ?.from('tax_rates')
          ?.insert([{
            name: formData?.name,
            rate: parseFloat(formData?.rate),
            description: formData?.description,
            is_default: formData?.is_default,
            is_active: true
          }]);
        if (error) throw error;
      }

      await fetchTaxRates();
      setShowForm(false);
      setSuccessMsg(editingRate ? 'Tax rate updated.' : 'Tax rate added.');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save tax rate.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (rate) => {
    try {
      await supabase?.from('tax_rates')?.update({ is_active: !rate?.is_active })?.eq('id', rate?.id);
      await fetchTaxRates();
    } catch (err) {
      console.error('Error toggling tax rate:', err);
    }
  };

  const handleSetDefault = async (rate) => {
    try {
      await supabase?.from('tax_rates')?.update({ is_default: false })?.eq('is_default', true);
      await supabase?.from('tax_rates')?.update({ is_default: true })?.eq('id', rate?.id);
      await fetchTaxRates();
      setSuccessMsg(`"${rate?.name}" set as default tax rate.`);
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error('Error setting default:', err);
    }
  };

  const handleDelete = async (rate) => {
    if (!window.confirm(`Delete "${rate?.name}"? This cannot be undone.`)) return;
    try {
      await supabase?.from('tax_rates')?.delete()?.eq('id', rate?.id);
      await fetchTaxRates();
    } catch (err) {
      console.error('Error deleting tax rate:', err);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 md:py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-1">Tax Rate Settings</h1>
            <p className="text-muted-foreground">Configure sales tax rates applied to invoices</p>
          </div>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={handleOpenAdd}>
            Add Tax Rate
          </Button>
        </div>

        {successMsg && (
          <div className="mb-4 bg-success/10 border border-success/20 rounded-lg p-3 flex items-center gap-2">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
            <span className="text-sm text-success">{successMsg}</span>
          </div>
        )}

        {/* Tax Rates Table */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Icon name="Percent" size={18} color="var(--color-primary)" />
            <h2 className="font-heading font-semibold text-foreground">Configured Tax Rates</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={28} color="var(--color-primary)" className="animate-spin" />
            </div>
          ) : taxRates?.length === 0 ? (
            <div className="p-12 text-center">
              <Icon name="Percent" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-40" />
              <p className="text-muted-foreground">No tax rates configured. Add your first tax rate.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Rate</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Default</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {taxRates?.map((rate) => (
                    <tr key={rate?.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-medium text-foreground">{rate?.name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-lg font-bold text-primary">{rate?.rate}%</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{rate?.description || '—'}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleToggleActive(rate)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                            rate?.is_active
                              ? 'bg-success/10 text-success hover:bg-success/20' :'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          <Icon name={rate?.is_active ? 'CheckCircle' : 'XCircle'} size={12} color="currentColor" />
                          {rate?.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        {rate?.is_default ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                            <Icon name="Star" size={12} color="currentColor" />
                            Default
                          </span>
                        ) : (
                          <button
                            onClick={() => handleSetDefault(rate)}
                            className="text-xs text-muted-foreground hover:text-primary transition-colors"
                          >
                            Set default
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenEdit(rate)}
                            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                            title="Edit"
                          >
                            <Icon name="Pencil" size={15} color="var(--color-muted-foreground)" />
                          </button>
                          <button
                            onClick={() => handleDelete(rate)}
                            className="p-1.5 rounded-lg hover:bg-error/10 transition-colors"
                            title="Delete"
                          >
                            <Icon name="Trash2" size={15} color="var(--color-error)" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Card */}
        <div className="mt-6 bg-primary/5 border border-primary/20 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-foreground mb-1">How Tax Rates Work</h3>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>The <strong>default</strong> tax rate is automatically applied when converting tickets to invoices.</li>
                <li>Managers can override the tax rate during invoice conversion.</li>
                <li>Inactive rates are hidden from selection but preserved for historical records.</li>
                <li>Tax is calculated on the subtotal after any discounts are applied.</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-md">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-heading font-semibold text-foreground">
                {editingRate ? 'Edit Tax Rate' : 'Add Tax Rate'}
              </h3>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-muted">
                <Icon name="X" size={18} color="var(--color-foreground)" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Input
                label="Name"
                placeholder="e.g. Standard Sales Tax"
                required
                value={formData?.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
              />
              <Input
                label="Rate (%)"
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="e.g. 8.5"
                required
                value={formData?.rate}
                onChange={(e) => setFormData(prev => ({ ...prev, rate: e?.target?.value }))}
              />
              <Input
                label="Description (optional)"
                placeholder="Brief description"
                value={formData?.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
              />
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData?.is_default}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_default: e?.target?.checked }))}
                  className="w-4 h-4 rounded border-input"
                />
                <span className="text-sm font-medium text-foreground">Set as default tax rate</span>
              </label>

              {error && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-center gap-2">
                  <Icon name="AlertCircle" size={15} color="var(--color-error)" />
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving}>Cancel</Button>
                <Button variant="default" loading={saving} disabled={saving} onClick={handleSave}>
                  {editingRate ? 'Save Changes' : 'Add Rate'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxRateSettings;
